import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaLinkedin, FaTwitter, FaEnvelope } from 'react-icons/fa';
import apiService from '../services/api';
import Loading from '../components/Loading';

const TeamPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await apiService.getTeamMembers();
      if (response.data.success) {
        setTeam(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Our Team - EduConsult</title>
        <meta name="description" content="Meet our team of experienced education counselors and admission experts dedicated to helping you achieve your academic goals." />
      </Helmet>

      {/* Hero */}
      <section className="hero-pattern pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Meet Our <span className="gradient-text">Expert Team</span>
            </h1>
            <p className="text-lg text-gray-600">
              Experienced professionals dedicated to guiding you towards academic success
            </p>
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {loading ? (
            <Loading message="Loading team members..." />
          ) : team.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {team.map((member) => (
                <div
                  key={member.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {/* Photo */}
                  <div className="relative h-80 overflow-hidden bg-gradient-to-br from-primary-100 to-secondary-100">
                    {member.photo_url ? (
                      <img
                        src={member.photo_url}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-8xl font-bold text-primary-300">
                          {member.name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {member.name}
                    </h3>
                    <p className="text-primary-600 font-medium mb-4">
                      {member.role}
                    </p>
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                      {member.description || 'Dedicated professional committed to student success.'}
                    </p>

                    {/* Social Links */}
                    <div className="flex gap-3">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-primary-600 hover:text-white transition-all"
                        >
                          <FaEnvelope />
                        </a>
                      )}
                      {member.linkedin_url && (
                        <a
                          href={member.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-primary-600 hover:text-white transition-all"
                        >
                          <FaLinkedin />
                        </a>
                      )}
                      {member.twitter_url && (
                        <a
                          href={member.twitter_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-primary-600 hover:text-white transition-all"
                        >
                          <FaTwitter />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No team members found.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Our expert team is ready to guide you towards your dream college
          </p>
          <a href="/register" className="btn bg-white text-primary-600 hover:bg-gray-100">
            Schedule a Consultation
          </a>
        </div>
      </section>
    </>
  );
};

export default TeamPage;
